import type { IconButtonProps } from "@mui/material/IconButton"

import { Logo } from "@/src/common/logo"

import NoSsr from "@mui/material/NoSsr"
import Avatar from "@mui/material/Avatar"
import SvgIcon from "@mui/material/SvgIcon"
import { useTheme } from "@mui/material/styles"
import { Box, Stack, Divider, Typography } from "@mui/material"

import { AnimateAvatar } from "src/common/animate"

// ----------------------------------------------------------------------

export type AccountButtonProps = IconButtonProps & {
    open: boolean
    photoURL: string
    displayName: string
    isExpanded: boolean
    /** When true, hide the workspace logo in collapsed sidebar (e.g. for internal users) */
    hideWorkspaceLogo?: boolean
}

export function AccountButton({
    open,
    photoURL,
    displayName,
    sx,
    onClick,
    isExpanded,
    hideWorkspaceLogo = false,
    ...other
}: AccountButtonProps) {
    const theme = useTheme()

    const renderFallback = (
        <Avatar
            sx={{
                width: 40,
                height: 40,
                border: `solid 2px ${theme.vars.palette.background.default}`,
            }}
        >
            <SvgIcon>
                <circle cx="12" cy="6" r="4" fill="currentColor" />
                <path
                    fill="currentColor"
                    d="M20 17.5c0 2.485 0 4.5-8 4.5s-8-2.015-8-4.5S7.582 13 12 13s8 2.015 8 4.5"
                    opacity="0.5"
                />
            </SvgIcon>
        </Avatar>
    )

    return (
        <NoSsr fallback={renderFallback}>
            {isExpanded ? (
                <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    onClick={(e) => onClick?.(e as any)}
                    sx={{
                        cursor: "pointer",
                        width: "100%",
                    }}
                >
                    <AnimateAvatar
                        slotProps={{
                            avatar: { src: photoURL, alt: displayName },
                            overlay: {
                                border: 1,
                                spacing: 2,
                                color: `conic-gradient(${theme.vars.palette.primary.main}, ${theme.vars.palette.warning.main}, ${theme.vars.palette.primary.main})`,
                            },
                        }}
                        sx={{ cursor: "pointer" }}
                    >
                        {displayName?.charAt(0).toUpperCase()}
                    </AnimateAvatar>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {displayName}
                    </Typography>
                </Stack>
            ) : (
                <Stack spacing={1} mt={1} sx={{ width: "unset" }}>
                    <AnimateAvatar
                        slotProps={{
                            avatar: { src: photoURL, alt: displayName },
                            overlay: {
                                border: 1,
                                spacing: 2,
                                color: `conic-gradient(${theme.vars.palette.primary.main}, ${theme.vars.palette.warning.main}, ${theme.vars.palette.primary.main})`,
                            },
                        }}
                        sx={{ cursor: "pointer" }}
                        onClick={(e) => onClick?.(e as any)}
                    >
                        {displayName?.charAt(0).toUpperCase()}
                    </AnimateAvatar>
                    {!hideWorkspaceLogo && (
                        <>
                            <Divider sx={{ margin: "6px 0", width: "100%" }} />
                            <Box onClick={onClick as any} sx={{ cursor: "pointer" }}>
                                <Logo />
                            </Box>
                        </>
                    )}
                </Stack>
            )}
        </NoSsr>
    )
}
